import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, Archive, ShieldCheck, Images } from "lucide-react";

export default function StoryWallInbox() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("story_wall_client_title"));
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [showName, setShowName] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/client/story-wall/moments-pending");
      setMoments(data?.moments || []);
    } catch { setMoments([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = (m) => { setDetail(m); setShowName(!!m.client_show_name); };

  const grant = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await api.post(`/client/story-wall/moments/${detail.id}/consent`, { consent: true, show_name: showName });
      setDetail(null);
      toast.success(t("story_wall_client_consent_thanks_title"), { description: t("story_wall_client_consent_thanks_body") });
      await load();
    } catch (e) { toast.error(apiError(e, t("something_went_wrong"))); } finally { setBusy(false); }
  };

  const decline = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await api.post(`/client/story-wall/moments/${detail.id}/revoke`);
      setDetail(null);
      await load();
    } catch (e) { toast.error(apiError(e, t("something_went_wrong"))); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="story-wall-inbox">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/story-wall")} data-testid="csw-inbox-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div>
          <h1 className="font-display text-2xl text-foreground">{t("story_wall_client_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("story_wall_client_sub")}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : moments.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-14 text-center">
          <Archive className="h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">{t("story_wall_client_empty")}</p>
          <button onClick={() => navigate("/app/story-wall")} data-testid="csw-inbox-browse-public" className="flex items-center gap-2 rounded-full border border-primary bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
            <Images className="h-4 w-4" /> {t("story_wall_browse_public")}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {moments.map((m) => (
            <button key={m.id} onClick={() => openDetail(m)} data-testid={`csw-inbox-card-${m.id}`}
              className="block w-full rounded-2xl p-4 text-left transition-transform hover:-translate-y-0.5" style={{ backgroundColor: m.color_accent || "hsl(var(--secondary))" }}>
              <div className="flex items-center justify-between">
                <span className="inline-block rounded-full bg-white/50 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-foreground">{t(`story_wall_kind_${m.kind}`)}</span>
                <ChevronRight className="h-4 w-4 text-foreground/60" />
              </div>
              {m.metric_label ? <p className="mt-1.5 font-display text-2xl text-foreground">{m.metric_label}</p> : null}
              <p className="mt-1 font-semibold text-foreground">{m.title}</p>
              <p className="mt-0.5 line-clamp-4 text-sm leading-snug text-foreground/90">{m.body}</p>
              <p className="mt-2 text-[11px] italic text-foreground/70">
                {m.coach_show_name && m.coach_name ? t("story_wall_by_coach", { name: m.coach_name }) : t("story_wall_by_coach_anonymous")}
              </p>
            </button>
          ))}
        </div>
      )}

      {detail ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDetail(null)} data-testid="csw-consent-modal">
          <div className="w-full max-w-md rounded-2xl bg-background p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-lg text-foreground">{t("story_wall_client_consent_title")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("story_wall_client_consent_sub")}</p>
            <div className="my-4 rounded-xl p-4" style={{ backgroundColor: detail.color_accent || "hsl(var(--secondary))" }}>
              {detail.metric_label ? <p className="font-display text-2xl text-foreground">{detail.metric_label}</p> : null}
              <p className="font-semibold text-foreground">{detail.title}</p>
              <p className="mt-0.5 text-sm text-foreground/90">{detail.body}</p>
            </div>
            <button onClick={() => setShowName((v) => !v)} data-testid="csw-show-name" className="flex w-full items-center gap-3 py-2 text-left">
              <span className={`flex h-6 w-11 items-center rounded-full p-0.5 transition-colors ${showName ? "bg-primary" : "bg-border"}`}>
                <span className={`h-5 w-5 rounded-full bg-white transition-transform ${showName ? "translate-x-5" : ""}`} />
              </span>
              <span className="flex-1">
                <span className="block text-sm font-medium text-foreground">{t("story_wall_show_name_lbl")}</span>
                <span className="block text-xs text-muted-foreground">{t("story_wall_show_name_client_sub")}</span>
              </span>
            </button>
            <div className="mt-2 flex items-start gap-2 rounded-xl bg-primary/10 p-3">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <p className="text-xs leading-relaxed text-foreground">{t("story_wall_privacy_note")}</p>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={decline} disabled={busy} data-testid="csw-inbox-decline" className="flex-1 rounded-full border border-border py-2.5 text-sm font-medium text-muted-foreground disabled:opacity-50">{t("story_wall_decline")}</button>
              <button onClick={grant} disabled={busy} data-testid="csw-inbox-grant" className="flex flex-1 items-center justify-center rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("story_wall_grant")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
